import _cloneDeep from "clone-deep"
import get from "lodash.get"
import { YYYYMMDD } from "./dateUtils.mjs"
import { getFilterFunction } from "./miscUtils.mjs"

const { getPrototypeOf } = Object

/**
 * Recursively traverses a nested object and flattens its properties into a target object.
 *
 * Nested keys are concatenated using dot notation.
 *
 * @param {object} currentNode - The current object node being traversed.
 * @param {object} target - The object that will hold the flattened key-value pairs.
 * @param {string} [flattenedKey] - The accumulated key path during recursion.
 * @param {object} [options={}] - Options for traversal.
 * @param {string[]} [options.skipNodesWithKey] - An array of property names to skip entirely during traversal.
 *        If a key matches one of these, its value will not be traversed or included in the output.
 * @param {boolean} [options.skipCircularReferences=false] - If `true`, prevents infinite recursion by skipping
 *        properties that would cause circular references. Uses the `ancestors` array internally.
 * @param {object[]} [options.ancestors=[]] - Internal use only; keeps track of visited nodes when
 *        `skipCircularReferences` is enabled. You generally do not need to set this manually.
 *
 * @example
 * const input = { a: { b: { c: 1 } }, d: 2 }
 * const result = {}
 * traverseAndFlatten(input, result)
 * // result is { "a.b.c": 1, "d": 2 }
 *
 * @example
 * const input = { a: { b: 1 }, secret: { x: 42 } }
 * const result = {}
 * traverseAndFlatten(input, result, undefined, { skipNodesWithKey: ["secret"] })
 * // result is { "a.b": 1 }
 */
function traverseAndFlatten(currentNode, target, flattenedKey, options = {}) {
  const { skipNodesWithKey, skipCircularReferences, ancestors = [] } = options

  // Prevent reentrancy / circular references
  // `ancestors` contains all currentNode ancestors.
  // If currentNode is already contained in it, it means currentNode is its own ancestor.
  // In other words, a loop is formed, and if we try to traverse it, we will for sure blow up the stack.
  if (skipCircularReferences) {
    if (ancestors.includes(currentNode)) return
    ancestors.push(currentNode)
  }

  for (const key in currentNode) {
    // eslint-disable-next-line no-prototype-builtins
    if (!currentNode.hasOwnProperty?.(key)) continue

    if (skipNodesWithKey?.includes(key)) break

    const newKey = flattenedKey === undefined ? key : flattenedKey + "." + key

    const value = currentNode[key]
    if (typeof value === "object") {
      traverseAndFlatten(value, target, newKey, { ...options, ancestors })
    } else {
      target[newKey] = value
    }
  }
}

/**
 * Flattens a deeply nested object into a single-level object using dot notation for nested keys.
 *
 * @param {object} obj - The object to flatten.
 * @param {object} [options={}] - Options for flattening.
 * @returns {object} A new object with flattened keys.
 *
 * @example
 * flattenObject({ a: { b: { c: 1 } }, d: 2 })
 * // Returns: { "a.b.c": 1, "d": 2 }
 */
function flattenObject(obj, options) {
  const flattenedObj = {}
  traverseAndFlatten(obj, flattenedObj, undefined, options)
  return flattenedObj
}

/**
 * @deprecated Use "toQueryString()" instead. This function will be removed in future versions.
 *
 * Converts an object into a URL search parameter string.
 *
 * Filters out `undefined`, `null`, empty strings, and empty arrays.
 * Handles arrays, dates (formatted as YYYYMMDD), and trims strings.
 *
 * @param {object} [object={}] - The object to convert.
 * @param {string} [prefix="?"] - The string to prepend before the search parameters.
 * @param {string} [paramSeparator="&"] - Separator between parameters.
 * @param {string} [fieldValueSeparator="="] - Separator between field names and their values.
 * @returns {string} A formatted search parameter string.
 *
 * @example
 * objectToSearchParam({ a: 1, b: "test" })
 * // Returns: "?a=1&b=test"
 *
 * @example
 * objectToSearchParam({ arr: [1, 2], date: new Date("2023-05-20") }, "", ";", ":")
 * // Returns something like: "arr:1,2;date:20230520"
 */
function objectToSearchParam(object = {}, prefix = "?", paramSeparator = "&", fieldValueSeparator = "=") {
  const str = Object.keys(object)
    .map(field => {
      let value = object[field]

      if (value === undefined || value === null) return
      if (typeof value === "string" && !value.trim()) return
      if (Array.isArray(value) && value.length === 0) return

      if (Array.isArray(object[field])) value = value.join(",")
      else if (value instanceof Date) value = YYYYMMDD(value)
      else if (typeof value === "string") value = value.trim()

      return `${field}${fieldValueSeparator}${value}`
    })
    .filter(it => it)
    .join(paramSeparator)
  return prefix && str ? prefix + str : str
}

/**
 * Safely retrieves a nested property from an object using dot notation.
 *
 * Returns `undefined` if any part of the path does not exist.
 *
 * @param {object} [obj={}] - The object to traverse.
 * @param {string} [path=""] - The dot-separated path string (e.g., "a.b.c").
 * @returns {*} The value at the specified path, or `undefined` if not found.
 *
 * @example
 * getObjProp({ a: { b: { c: 42 } } }, "a.b.c")
 * // Returns: 42
 *
 * @example
 * getObjProp({ a: { b: 10 } }, "a.b.c")
 * // Returns: undefined
 */
function getObjProp(obj = {}, path = "") {
  const paths = path.split(".")
  let current = obj
  let i

  for (i = 0; i < paths.length; i++) {
    if (current[paths[i]] === undefined) {
      return undefined
    } else {
      current = current[paths[i]]
    }
  }

  return current
}

/**
 * Concatenates the values of specific nested properties from an object into a single string.
 *
 * Each value is retrieved using dot notation paths. If a path does not resolve to a value, it is skipped (treated as an empty string).
 *
 * @param {object} [obj={}] - The object to extract values from.
 * @param {string[]} [paths=[]] - An array of dot-separated property paths (e.g., ["a.b", "c.d"]).
 * @returns {string} A concatenated string of the extracted values.
 *
 * @example
 * getValuesString({ a: { b: "hello" }, c: { d: "world" } }, ["a.b", "c.d"])
 * // Returns: "helloworld"
 *
 * @example
 * getValuesString({ a: { b: 1 }, x: "!" }, ["a.b", "x"])
 * // Returns: "1!"
 *
 * @example
 * getValuesString({ a: {} }, ["a.b", "a.c"])
 * // Returns: ""
 */
function getValuesString(obj = {}, paths = []) {
  let jsonString = ""
  let i
  for (i = 0; i < paths.length; i++) {
    jsonString += getObjProp(obj, paths[i]) ?? ""
  }
  return jsonString
}

/**
 * Filters an array of objects based on field-value pairs, applying locale-aware matching.
 *
 * For each key in the `filters` object, if the filter value is non-empty,
 * a locale-specific match function is generated (via `getFilterFunction`)
 * and applied to the corresponding field of each object.
 *
 * @param {object[]} objects - Array of objects to filter.
 * @param {object} filters - Key-value pairs where the key is a field name and the value is a search string.
 * @param {string} [locale] - Optional locale string used by `getFilterFunction` to perform locale-sensitive comparisons.
 * @returns {object[]} A filtered array of objects that match all the specified filters.
 *
 * @example
 * filterObjectsByFieldsValues(
 *   [{ name: "Alice" }, { name: "Bob" }],
 *   { name: "ali" },
 *   "en"
 * )
 * // Might return: [{ name: "Alice" }] depending on the filter logic.
 *
 * @example
 * filterObjectsByFieldsValues(
 *   [{ city: "Paris" }, { city: "Berlin" }],
 *   { city: "par" },
 *   "fr"
 * )
 * // Might return: [{ city: "Paris" }] depending on filter logic.
 */
function filterObjectsByFieldsValues(objects, filters, locale) {
  return objects.filter(obj => {
    return Object.keys(filters).every(key => {
      if (filters[key] && filters[key].trim() !== "") {
        const includes = getFilterFunction(filters[key], locale)
        return includes(obj[key])
      }
      return true
    })
  })
}

/**
 * Recursively replaces the value of all occurrences of a specific key within a nested object.
 *
 * This function traverses the object deeply and replaces any matching key's value
 * (excluding functions) with the specified `newValue`.
 *
 * @param {object} obj - The object to modify.
 * @param {string} keyToReplace - The key whose value should be replaced.
 * @param {*} newValue - The new value to assign to matching keys.
 * @returns {object} The modified object with updated values.
 *
 * @example
 * replaceValuesForKey({ a: 1, b: { a: 2 } }, 'a', 99)
 * // Returns: { a: 99, b: { a: 99 } }
 *
 * @example
 * replaceValuesForKey({ user: { name: "Alice", age: 25 }, age: 30 }, 'age', 0)
 * // Returns: { user: { name: "Alice", age: 0 }, age: 0 }
 */
function replaceValuesForKey(obj, keyToReplace, newValue) {
  for (const key in obj) {
    if (typeof obj[key] === "function") continue

    if (typeof obj[key] === "object" && obj[key] !== null) {
      replaceValuesForKey(obj[key], keyToReplace, newValue) // Recursively call for nested objects
    } else if (key === keyToReplace) {
      obj[key] = newValue
    }
  }
  return obj
}

/**
 * Checks whether the given value is a plain object.
 *
 * This function ensures that the input is:
 * - of type 'object'
 * - not an array
 * - not a Date instance
 * - not null
 *
 * @param {*} obj - The value to check.
 * @returns {boolean} True if the value is a plain object, false otherwise.
 *
 * @example
 * isObject({ key: "value" }) // true
 * isObject([1, 2, 3]) // false
 * isObject(null) // false
 * isObject(new Date()) // false
 */
function isObject(obj) {
  return typeof obj === "object" && !Array.isArray(obj) && !(obj instanceof Date) && obj !== null
}

/**
 * Returns the internal JavaScript [[Class]] of a given value as a lowercase string.
 *
 * This is more reliable than `typeof` for complex types (e.g., `Array`, `Date`, `Map`),
 * and uses a cached lookup for performance.
 *
 * @param {*} thing - The value whose type is to be determined.
 * @returns {string} A lowercase string representing the type (e.g., "array", "object", "string").
 *
 * @example
 * kindOf([]) // "array"
 * kindOf(null) // "null"
 * kindOf(new Map()) // "map"
 */
const kindOf = (cache => thing => {
  const str = toString.call(thing)
  return cache[str] || (cache[str] = str.slice(8, -1).toLowerCase())
})(Object.create(null))

/**
 * Checks if a value is a plain object (i.e., created by `{}`, `new Object()`, or `Object.create(null)`).
 *
 * @param {*} val - The value to check.
 * @returns {boolean} Returns `true` if the value is a plain object, otherwise `false`.
 *
 * @example
 * isPlainObject({}); // true
 * isPlainObject(Object.create(null)); // true
 * isPlainObject(new Object()); // true
 * isPlainObject([]); // false
 * isPlainObject(null); // false
 * isPlainObject(new Date()); // false
 */
function isPlainObject(val) {
  if (kindOf(val) !== "object") return false

  const prototype = getPrototypeOf(val)
  return (
    (prototype === null || prototype === Object.prototype || Object.getPrototypeOf(prototype) === null) &&
    !(Symbol.toStringTag in val) &&
    !(Symbol.iterator in val)
  )
}

/**
 * Creates a new object by removing all properties from the input object
 * whose values are `null` or `undefined`.
 *
 * @param {Object} obj - The object to prune.
 * @returns {Object} A new object with all `null` and `undefined` values removed.
 *
 * @example
 * pruneObject({ a: 1, b: null, c: undefined, d: "text" });
 * // Returns: { a: 1, d: "text" }
 *
 * @example
 * pruneObject({ x: null, y: 0, z: false });
 * // Returns: { y: 0, z: false }
 */
function pruneObject(obj) {
  return Object.entries(obj).reduce((prunedObj, [key, value]) => {
    return [null, undefined].includes(value) ? prunedObj : ((prunedObj[key] = value), prunedObj)
  }, {})
}

/**
 * Estimates the approximate memory size in bytes of an object, including nested objects and arrays.
 * Handles circular references by tracking visited objects to avoid infinite loops.
 *
 * @param {Object} object - The object to measure.
 * @returns {number} The estimated size in bytes.
 *
 * @example
 * getObjectSize({ a: 1, b: true, c: "hello" });
 * // Returns a number representing the estimated byte size.
 *
 * @example
 * const obj = { a: [1, 2, 3], b: { c: "text" } };
 * getObjectSize(obj);
 * // Returns the estimated size including nested arrays and objects.
 */
function getObjectSize(object) {
  const objectList = new Set() // To track visited objects and prevent circular references
  const stack = [object]
  let bytes = 0

  while (stack.length > 0) {
    const value = stack.pop()

    // Skip if the value is already visited (circular reference)
    if (value !== null && typeof value === "object") {
      if (objectList.has(value)) {
        continue
      }
      objectList.add(value)
    }

    switch (typeof value) {
      case "boolean":
        bytes += 4
        break
      case "number":
        bytes += 8
        break
      case "string":
        bytes += value.length * 2 // Roughly 2 bytes per character
        break
      case "object":
        if (Array.isArray(value)) {
          for (let i = 0; i < value.length; i++) {
            stack.push(value[i])
          }
        } else if (value !== null) {
          for (const key in value) {
            // Account for property keys
            bytes += key.length * 2
            stack.push(value[key])
          }
        }
        break
      default:
        break
    }
  }

  return bytes
}

/**
 * Deep-clones a value (objects, arrays, dates, etc.) via `clone-deep`.
 *
 * @template T
 * @param {T} object - Value to clone.
 * @returns {T} Deep copy of `object`.
 */
function cloneDeep(object) {
  return _cloneDeep(object)
}

/**
 * Compares two objects and identifies differences in specified keys.
 *
 * @param {Object} params - Parameters object.
 * @param {Object} params.obj1 - The first object to compare.
 * @param {Object} params.obj2 - The second object to compare.
 * @param {string[]} [params.keys=[]] - Array of keys to compare in the objects.
 * @param {boolean} [params.allKeys=false] - If `true`, returns all differing keys; if `false`, returns only the first difference found.
 * @returns {{ diffKey: { key: string, val1: any, val2: any } | null, diffKeys: Array<{ key: string, val1: any, val2: any }> }}
 * An object containing the first differing key and an array of all differing keys with their values.
 *
 * @example
 * objectsDiff({
 *   obj1: { a: 1, b: 2, c: 3 },
 *   obj2: { a: 1, b: 4, c: 3 },
 *   keys: ["a", "b", "c"],
 *   allKeys: false
 * });
 * // Returns:
 * // {
 * //   diffKey: { key: "b", val1: 2, val2: 4 },
 * //   diffKeys: [{ key: "b", val1: 2, val2: 4 }]
 * // }
 *
 * @example
 * objectsDiff({
 *   obj1: { a: { x: 1 }, b: 2 },
 *   obj2: { a: { x: 2 }, b: 2 },
 *   keys: ["a", "b"],
 *   allKeys: true
 * });
 * // Returns:
 * // {
 * //   diffKey: null,
 * //   diffKeys: [
 * //     { key: "a", val1: '{"x":1}', val2: '{"x":2}' }
 * //   ]
 * // }
 */
function objectsDiff({ obj1, obj2, keys = [], allKeys = false }) {
  const diff = { diffKey: null, diffKeys: [] }

  if (!obj1 || typeof obj1 !== "object") return diff
  if (!obj2 || typeof obj2 !== "object") return diff
  if (!Array.isArray(keys)) return diff

  for (const key of keys) {
    if (typeof key !== "string") continue

    let val1 = get(obj1, key)
    let val2 = get(obj2, key)

    // If val1 is an object, only then we stringify val2 to check their equality to save a bit on performance.
    // If val1 is not an object, then this is not necessary to stringify val2 as both values would never match already.
    if (typeof val1 === "object") {
      val1 = JSON.stringify(val1)
      if (typeof val2 === "object") {
        val2 = JSON.stringify(val2)
      }
    }

    if (val1 !== val2) {
      diff.diffKeys.push({ key, val1, val2 })
      if (!allKeys) {
        diff.diffKey = { key, val1, val2 }
        return diff
      }
    }
  }
  return diff
}

/**
 * Checks if a given value is a Promise (or "thenable").
 *
 * @param {*} obj - The value to check.
 * @returns {boolean} True if the value is a Promise or has a `.then` method, false otherwise.
 */
function isPromise(obj) {
  return !!obj && (typeof obj === "object" || typeof obj === "function") && typeof obj.then === "function"
}

export {
  cloneDeep,
  filterObjectsByFieldsValues,
  flattenObject,
  getObjectSize,
  getObjProp,
  getValuesString,
  isObject,
  isPlainObject,
  objectsDiff,
  objectToSearchParam,
  pruneObject,
  replaceValuesForKey,
  isPromise,
}
